import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, clearToken, getToken, setToken } from "@/lib/api";
import { getTelegramWebApp, isInsideTelegram } from "@/lib/telegram";
import type { AuthResponse, User } from "@/types/api";

type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  error: string | null;
  loginWithTelegram: () => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setStatus("unauthenticated");
      return;
    }
    try {
      const me = await api.get<User>("/auth/me");
      setUser(me);
      setStatus("authenticated");
    } catch {
      logout();
    }
  }, [logout]);

  const loginWithPassword = useCallback(async (username: string, password: string) => {
    setStatus("loading");
    setError(null);
    try {
      const resp = await api.post<AuthResponse>("/auth/dev-login", { username, password });
      setToken(resp.token);
      setUser(resp);
      setStatus("authenticated");
    } catch (err) {
      setStatus("unauthenticated");
      const msg = err instanceof Error ? err.message : "Ошибка входа";
      setError(msg);
      throw err;
    }
  }, []);

  const loginWithTelegram = useCallback(async () => {
    setStatus("loading");
    setError(null);

    const tg = getTelegramWebApp();
    if (!tg || !tg.initData) {
      setStatus("unauthenticated");
      setError("Открой приложение через Telegram");
      return;
    }

    try {
      const resp = await api.post<AuthResponse>("/auth/telegram-webapp-login", {
        init_data: tg.initData,
      });
      setToken(resp.token);
      setUser(resp);
      setStatus("authenticated");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Ошибка входа");
    }
  }, []);

  useEffect(() => {
    if (isInsideTelegram()) {
      // First try stored token; if invalid, fall back to Telegram login.
      void (async () => {
        const token = getToken();
        if (token) {
          try {
            const me = await api.get<User>("/auth/me");
            setUser(me);
            setStatus("authenticated");
            return;
          } catch {
            clearToken();
          }
        }
        await loginWithTelegram();
      })();
    } else if (getToken()) {
      void refresh();
    } else {
      setStatus("unauthenticated");
    }
  }, [loginWithTelegram, refresh]);

  useEffect(() => {
    const onUnauth = () => logout();
    window.addEventListener("sp:unauthorized", onUnauth);
    return () => window.removeEventListener("sp:unauthorized", onUnauth);
  }, [logout]);

  // Keep the hero rating (and is_admin / is_trainer) fresh:
  //   • whenever any mutation that touches a rating fires `sp:rating-changed`
  //   • when the tab regains focus
  //   • as a safety net, every 60 s while the tab is open
  useEffect(() => {
    const onRating = () => {
      if (getToken()) void refresh();
    };
    const onFocus = () => {
      if (getToken()) void refresh();
    };
    window.addEventListener("sp:rating-changed", onRating);
    window.addEventListener("focus", onFocus);
    const t = window.setInterval(() => {
      if (document.visibilityState === "visible" && getToken()) void refresh();
    }, 60_000);
    return () => {
      window.removeEventListener("sp:rating-changed", onRating);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(t);
    };
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, error, loginWithTelegram, loginWithPassword, logout, refresh }),
    [user, status, error, loginWithTelegram, loginWithPassword, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
